import React from 'react';
import { CircularProgress } from '@mui/material';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';

import type { ScriptStep } from '@shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@shared/interfaces/polly';
import { replaceAssistantPlaceholders } from '@shared/utils/assistantScript';
import type { AudioPlaybackKey } from '../hooks/useAudioPreview';
import { PreviewButtonWrapper, PreviewButton } from './styled';

interface StepPreviewButtonProps {
  step: ScriptStep;
  index: number;
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  audioPlaybackKey: AudioPlaybackKey | null;
  isAudioPlaying: boolean;
  userFirstName?: string;
  mapName?: string;
  onPlay: (text: string, index: number) => void;
  onStop: () => void;
}

const StepPreviewButton = ({
  step,
  index,
  activeLanguage,
  effectiveBaseLanguage,
  audioPlaybackKey,
  isAudioPlaying,
  userFirstName,
  mapName,
  onPlay,
  onStop,
}: StepPreviewButtonProps) => {
  const isBaseLang = activeLanguage === effectiveBaseLanguage;

  const getPreviewText = (): string => {
    const raw = isBaseLang
      ? step.translations?.[effectiveBaseLanguage] ?? step.text
      : step.translations?.[activeLanguage] ?? step.text;
    return replaceAssistantPlaceholders(raw, userFirstName, mapName);
  };

  const isDisabled = (): boolean => {
    const hasText = isBaseLang
      ? Boolean(step.translations?.[effectiveBaseLanguage] ?? step.text)
      : Boolean(step.translations?.[activeLanguage]);
    return (
      !hasText || (audioPlaybackKey !== null && audioPlaybackKey !== index)
    );
  };

  const { t } = useTranslation('common');

  const handleClick = () => {
    if (audioPlaybackKey === index && isAudioPlaying) {
      onStop();
    } else {
      onPlay(getPreviewText(), index);
    }
  };

  const renderContent = () => {
    if (audioPlaybackKey === index && !isAudioPlaying) {
      return <CircularProgress size={12} color="inherit" sx={{ mr: 0.5 }} />;
    }

    if (audioPlaybackKey === index && isAudioPlaying) {
      return (
        <>
          <StopIcon sx={{ fontSize: 13, mr: 0.5 }} />
          {t('assistant.scriptDialog.previewStop')}
        </>
      );
    }

    return (
      <>
        <PlayIcon sx={{ fontSize: 13, mr: 0.5 }} />
        {t('assistant.scriptDialog.previewPlay')}
      </>
    );
  };

  return (
    <PreviewButtonWrapper onClick={(e) => e.stopPropagation()}>
      <PreviewButton
        variant="outlined"
        size="small"
        onClick={handleClick}
        disabled={isDisabled()}
        data-testid={`script-step-${index}-preview-btn`}
      >
        {renderContent()}
      </PreviewButton>
    </PreviewButtonWrapper>
  );
};

export default StepPreviewButton;
